import React, { useEffect, useMemo, useState } from "react";
import {
  categoriesResource,
  clientsResource,
  departmentsResource,
  getDataHome,
  salesResource,
  sellersResource,
} from "../../helpers/configRoutes/backend_helper";
import { formatCurrencyUsd } from "../../helpers/Formatos";
import {
  AreaChart, Area,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { currencyDecimal, currencyUSD } from "../../helpers/forms/formater";
import CustomSelect from "../../components/form/CustomSelect";
import CustomButton from "../../components/form/CustomButton";
import CustomCalendarRange from "../../components/form/CustomCalendarRange";

// Paleta alineada al config (fr-primary, fr-accent, fr-success, fr-warning, fr-danger)
const COLORS = ["#078be3", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"];

const Home = () => {
  const [home, setHome] = useState(null);
  const [clients, setClients] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, seDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    seller_id: '',
    client_id: '',
    servicio: '',
    search: '',
    date_start: '',
    date_end: '',
    department_id: '',
    category_id: '',
  });

  useEffect(() => {
    const getHome = async () => {
      try {
        const response = await getDataHome();
        let perClient = response.ventas_por_cliente.map((v) => ({
          cliente: v.cliente,
          total: parseFloat(v.total),
          sales_count: v.sales_count,
        }));
        setHome({
          total_facturado: response.total_facturado,
          facturas_emitidas: response.facturas_emitidas,
          ticket_promedio: response.ticket_promedio,
          ventas_por_dia: response.ventas_por_dia,
          ventas_por_cliente: perClient,
          top_productos: response.top_productos,
          // Datos del back para reemplazar mocks
          clientes_activos: response.clientes_activos || { actual: 0, anterior: 0 },
          variacion_facturas: response.variacion_facturas ?? 0,
          variacion_ticket: response.variacion_ticket ?? 0,
          meta_mes: response.meta_mes || { meta: 0, alcanzado: 0 },
          seguimiento_clientes: response.seguimiento_clientes || [],
          clientes_en_riesgo: response.clientes_en_riesgo || [],
          top_vendedores: response.top_vendedores || [],
          productos_baja_rotacion: response.productos_baja_rotacion || [],
        });
      } catch (e) {
        console.error("Error cargando estadísticas:", e);
      }
    };
    getHome();
  }, []);

  useEffect(() => {
    const getCatalogs = async () => {
      const response = await clientsResource('get', { params: { select: true } });
      setClients(response.clients);
      const responseSellers = await sellersResource('get', { params: { select: true } });
      setSellers(responseSellers.sellers);
      const responseCategories = await categoriesResource('get', { params: { select: true } });
      const responseDepartments = await departmentsResource('get', { params: { select: true } });
      setCategories(responseCategories.categories);
      seDepartments(responseDepartments.departments);
    };
    getCatalogs();
  }, []);

  const getReports = async () => {
    setLoading(true);
    let config = {
      params: filters,
      headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      responseType: "blob",
    };
    const exportSalidas = await salesResource('get', config);
    const url = URL.createObjectURL(new Blob([exportSalidas]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Reporte de ventas.xlsx');
    document.body.appendChild(link);
    link.click();
    setLoading(false);
  };

  // ===== Cálculos derivados de datos reales =====
  const insights = useMemo(() => {
    if (!home?.ventas_por_dia?.length) return null;

    const ventas = home.ventas_por_dia.map(v => ({
      fecha: v.fecha,
      total: parseFloat(v.total),
    }));

    // Mes actual vs mes anterior (basado en la última fecha disponible)
    const last = new Date(ventas[ventas.length - 1].fecha);
    const yLast = last.getFullYear();
    const mLast = last.getMonth();
    const prevMonth = mLast === 0 ? 11 : mLast - 1;
    const yPrev = mLast === 0 ? yLast - 1 : yLast;

    const sumMonth = (y, m) => ventas
      .filter(v => {
        const d = new Date(v.fecha);
        return d.getFullYear() === y && d.getMonth() === m;
      })
      .reduce((a, b) => a + b.total, 0);

    const totalMesActual = sumMonth(yLast, mLast);
    const totalMesAnterior = sumMonth(yPrev, prevMonth);
    const variacionMoM = totalMesAnterior > 0
      ? ((totalMesActual - totalMesAnterior) / totalMesAnterior) * 100
      : 0;

    // Mejor día
    const mejorDia = ventas.reduce((max, v) => v.total > max.total ? v : max, ventas[0]);

    // Distribución por día de la semana
    const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const porDow = dias.map(d => ({ dia: d, total: 0, count: 0 }));
    ventas.forEach(v => {
      const dow = new Date(v.fecha).getDay();
      porDow[dow].total += v.total;
      porDow[dow].count += 1;
    });
    const mejorDow = porDow.reduce((max, v) => v.total > max.total ? v : max, porDow[0]);

    // Últimos 60 días para la gráfica principal
    const ultimosDias = ventas.slice(-60);

    // Concentración top 5 clientes
    const totalClientes = home.ventas_por_cliente.reduce((a, b) => a + b.total, 0);

    return {
      totalMesActual,
      totalMesAnterior,
      variacionMoM,
      mejorDia,
      porDow,
      mejorDow,
      ultimosDias,
      totalClientes,
    };
  }, [home]);

  // ===== Datos del back ya alineados al UI =====
  const dashboard = useMemo(() => ({
    metaMes: home?.meta_mes || { meta: 0, alcanzado: 0 },
    clientesActivos: home?.clientes_activos || { actual: 0, anterior: 0 },
    seguimientoClientes: home?.seguimiento_clientes || [],
    clientesEnRiesgo: home?.clientes_en_riesgo || [],
    topVendedores: home?.top_vendedores || [],
    productosBajaRotacion: home?.productos_baja_rotacion || [],
  }), [home]);

  // ===== Helpers UI =====
  const VariationBadge = ({ value }) => {
    const positive = value >= 0;
    return (
      <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded
        ${positive ? 'text-fr-success bg-fr-success/10' : 'text-fr-danger bg-fr-danger/10'}`}>
        <span className="material-symbols-outlined text-[14px]">
          {positive ? 'trending_up' : 'trending_down'}
        </span>
        {Math.abs(value).toFixed(1)}%
      </span>
    );
  };

  if (!home) {
    return (
      <div className="w-full p-10 flex items-center justify-center">
        <div className="flex items-center gap-3 text-fr-dark/60">
          <span className="material-symbols-outlined animate-spin">progress_activity</span>
          <span className="text-sm">Cargando estadísticas...</span>
        </div>
      </div>
    );
  }

  const variacionFacturas = home.variacion_facturas ?? 0;
  const variacionTicket = home.variacion_ticket ?? 0;
  const variacionClientes = dashboard.clientesActivos.anterior > 0
    ? ((dashboard.clientesActivos.actual - dashboard.clientesActivos.anterior) / dashboard.clientesActivos.anterior) * 100
    : 0;
  const progresoMeta = dashboard.metaMes.meta > 0
    ? (dashboard.metaMes.alcanzado / dashboard.metaMes.meta) * 100
    : 0;

  return (
    <div className="w-full px-4 md:px-6 lg:px-8 py-6 space-y-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] text-fr-dark/40 uppercase">Panel ejecutivo</p>
          <h1 className="text-2xl md:text-3xl font-bold text-fr-dark tracking-tight mt-1">
            Resumen de ventas
          </h1>
          <p className="text-sm text-fr-dark/60 mt-1">
            Información de desempeño, seguimiento y oportunidades comerciales.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-fr-dark/60">
          <span className="material-symbols-outlined text-[18px] text-fr-primary">schedule</span>
          Actualizado {new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
        </div>
      </div>

      {/* Filtros globales */}
      <section className="bg-fr-light border border-fr-muted rounded-xl shadow-sm">
        <div className="px-4 py-3 border-b border-fr-muted flex items-center gap-2">
          <span className="material-symbols-outlined text-[20px] text-fr-primary">filter_alt</span>
          <h2 className="text-sm font-semibold text-fr-dark tracking-tight">Filtros</h2>
          <span className="text-[11px] text-fr-dark/50 ml-auto">Aplican a todo el panel</span>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <CustomSelect isClearable className="text-sm" holder="Vendedor"
              options={sellers}
              value={filters.seller_id}
              handleChange={(e) => setFilters({ ...filters, seller_id: e.target.value })}
            />
            <CustomSelect isClearable className="text-sm" holder="Cliente"
              options={clients}
              value={filters.client_id}
              handleChange={(e) => setFilters({ ...filters, client_id: e.target.value })}
            />
            <CustomSelect isClearable className="text-sm" holder="Área"
              options={categories}
              value={filters.category_id}
              handleChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
            />
            <CustomSelect isClearable className="text-sm" holder="Departamento"
              options={departments}
              value={filters.department_id}
              handleChange={(e) => setFilters({ ...filters, department_id: e.target.value })}
            />
            <CustomCalendarRange isClearable className="text-sm" holder="Fechas Rango"
              valueStart={filters.date_start}
              valueEnd={filters.date_end}
              filters={filters}
              setFilters={setFilters}
            />
          </div>
          {/* Atajos de fecha */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold tracking-[0.15em] text-fr-dark/40 uppercase mr-1">Rápido:</span>
            {['Hoy', 'Ayer', 'Esta semana', 'Este mes', 'Mes anterior', 'Últimos 30 días', 'Año actual'].map((label) => (
              <button
                key={label}
                type="button"
                className="text-xs px-3 py-1.5 rounded-lg border border-fr-muted bg-fr-light hover:bg-fr-secondary text-fr-dark/70 hover:text-fr-dark transition-colors"
              >
                {label}
              </button>
            ))}
            <CustomButton
              onClick={() => getReports()}
              loading={loading}
              textLoad={'Generando...'}
              className="ml-auto bg-fr-primary hover:bg-fr-primary-hover text-fr-light text-xs px-3 py-1.5 rounded-lg shadow-sm transition-colors flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-[16px]">download</span>
              Exportar Excel
            </CustomButton>
          </div>
        </div>
      </section>

      {/* KPIs principales */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon="payments"
          label="Total facturado"
          value={formatCurrencyUsd(home.total_facturado)}
          variation={insights?.variacionMoM}
          hint="vs mes anterior"
          accent="primary"
        />
        <KpiCard
          icon="receipt_long"
          label="Facturas emitidas"
          value={home.facturas_emitidas.toLocaleString('es-MX')}
          variation={variacionFacturas}
          hint="vs período anterior"
          accent="accent"
        />
        <KpiCard
          icon="confirmation_number"
          label="Ticket promedio"
          value={formatCurrencyUsd(home.ticket_promedio)}
          variation={variacionTicket}
          hint="vs mes anterior"
          accent="success"
        />
        <KpiCard
          icon="groups"
          label="Clientes activos"
          value={dashboard.clientesActivos.actual}
          variation={variacionClientes}
          hint="vs mes anterior"
          accent="warning"
        />
      </section>

      {/* Insights row: Meta + Mejor día + Mejor día de la semana */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Meta del mes */}
        <div className="bg-fr-light border border-fr-muted rounded-xl shadow-sm p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-semibold tracking-[0.15em] text-fr-dark/40 uppercase">Meta del mes</p>
              <p className="text-2xl font-bold text-fr-dark tracking-tight mt-1">
                {progresoMeta.toFixed(1)}%
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-fr-primary/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-fr-primary text-[22px]">flag</span>
            </div>
          </div>
          <div className="mt-4 h-2 bg-fr-secondary rounded-full overflow-hidden">
            <div
              className="h-full bg-fr-primary rounded-full transition-all duration-500"
              style={{ width: `${Math.min(progresoMeta, 100)}%` }}
            ></div>
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-fr-dark/60">
            <span>Alcanzado: <span className="font-semibold text-fr-dark">{formatCurrencyUsd(dashboard.metaMes.alcanzado)}</span></span>
            <span>Meta: <span className="font-semibold text-fr-dark">{formatCurrencyUsd(dashboard.metaMes.meta)}</span></span>
          </div>
        </div>

        {/* Mejor día del mes */}
        <div className="bg-fr-light border border-fr-muted rounded-xl shadow-sm p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-semibold tracking-[0.15em] text-fr-dark/40 uppercase">Mejor día (rango)</p>
              <p className="text-2xl font-bold text-fr-dark tracking-tight mt-1">
                {insights?.mejorDia ? formatCurrencyUsd(insights.mejorDia.total) : '—'}
              </p>
              <p className="text-xs text-fr-dark/60 mt-1">
                {insights?.mejorDia ? new Date(insights.mejorDia.fecha).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'}
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-fr-success/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-fr-success text-[22px]">trophy</span>
            </div>
          </div>
        </div>

        {/* Día de la semana más fuerte */}
        <div className="bg-fr-light border border-fr-muted rounded-xl shadow-sm p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-semibold tracking-[0.15em] text-fr-dark/40 uppercase">Día más fuerte</p>
              <p className="text-2xl font-bold text-fr-dark tracking-tight mt-1">
                {insights?.mejorDow?.dia || '—'}
              </p>
              <p className="text-xs text-fr-dark/60 mt-1">
                {insights?.mejorDow ? formatCurrencyUsd(insights.mejorDow.total) : ''} acumulados
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-fr-warning/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-fr-warning text-[22px]">calendar_month</span>
            </div>
          </div>
        </div>
      </section>

      {/* Gráfica principal: Ventas por día (área con gradiente) */}
      <section className="bg-fr-light border border-fr-muted rounded-xl shadow-sm">
        <div className="px-5 py-4 border-b border-fr-muted flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-fr-dark tracking-tight">Tendencia de ventas</h2>
            <p className="text-xs text-fr-dark/50 mt-0.5">Últimos 60 días registrados</p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-fr-dark/60">
            <span className="w-2.5 h-2.5 rounded-full bg-fr-primary"></span> Ventas
          </div>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={insights?.ultimosDias || []}>
              <defs>
                <linearGradient id="colorVentas" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#078be3" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#078be3" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="fecha" tick={{ fontSize: 11, fill: '#6b7280' }}
                tickFormatter={(v) => new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}
              />
              <YAxis tick={{ fontSize: 11, fill: '#6b7280' }}
                tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
              />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: '1px solid #d1d5db', fontSize: 12 }}
                formatter={(v) => formatCurrencyUsd(v)}
                labelFormatter={(v) => new Date(v).toLocaleDateString('es-MX', { dateStyle: 'medium' })}
              />
              <Area type="monotone" dataKey="total" stroke="#078be3" strokeWidth={2.5}
                fill="url(#colorVentas)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Row: Top clientes + Top productos */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Top clientes (donut) */}
        <div className="bg-fr-light border border-fr-muted rounded-xl shadow-sm">
          <div className="px-5 py-4 border-b border-fr-muted">
            <h2 className="text-sm font-semibold text-fr-dark tracking-tight">Top 5 clientes</h2>
            <p className="text-xs text-fr-dark/50 mt-0.5">Concentración de cartera</p>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={home.ventas_por_cliente}
                  dataKey="total"
                  nameKey="cliente"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                >
                  {home.ventas_por_cliente.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #d1d5db', fontSize: 12 }}
                  formatter={(v) => currencyUSD(v)}
                />
              </PieChart>
            </ResponsiveContainer>
            <ul className="mt-3 space-y-2">
              {home.ventas_por_cliente.map((c, i) => (
                <li key={i} className="flex items-center gap-2 text-xs">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }}></span>
                  <span className="truncate text-fr-dark/80 flex-1">{c.cliente}</span>
                  <span className="font-semibold text-fr-dark">{currencyUSD(c.total)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Top productos */}
        <div className="lg:col-span-2 bg-fr-light border border-fr-muted rounded-xl shadow-sm">
          <div className="px-5 py-4 border-b border-fr-muted flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-fr-dark tracking-tight">Top productos más vendidos</h2>
              <p className="text-xs text-fr-dark/50 mt-0.5">Ordenados por monto facturado</p>
            </div>
          </div>
          <div className="p-4 space-y-2">
            {home.top_productos.map((prod, i) => {
              const max = parseFloat(home.top_productos[0].total);
              const percent = (parseFloat(prod.total) / max) * 100;
              return (
                <div key={i} className="group">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-7 h-7 shrink-0 rounded-lg bg-fr-primary/10 text-fr-primary text-xs font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-fr-dark truncate">{prod.producto}</p>
                        <p className="text-[11px] text-fr-dark/50">{currencyDecimal(prod.sales_quantity)} unidades</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-fr-dark whitespace-nowrap">{currencyUSD(prod.total)}</span>
                  </div>
                  <div className="h-1.5 bg-fr-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-fr-primary rounded-full transition-all duration-500 group-hover:bg-fr-accent"
                      style={{ width: `${percent}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Seguimiento de clientes (recompra anual) */}
      <section className="bg-fr-light border border-fr-muted rounded-xl shadow-sm">
        <div className="px-5 py-4 border-b border-fr-muted flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-fr-primary/10 flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-fr-primary text-[22px]">repeat</span>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-fr-dark tracking-tight">Seguimiento de clientes — Recompra anual</h2>
              <p className="text-xs text-fr-dark/50 mt-0.5">
                Clientes que en este mismo mes hace 1 año compraron un producto y aún no lo han vuelto a comprar.
              </p>
            </div>
          </div>
          <span className="text-[10px] font-semibold tracking-[0.15em] uppercase px-2 py-1 rounded bg-fr-warning/10 text-fr-warning shrink-0">
            Oportunidad
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-fr-secondary text-fr-dark/60 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3 text-left font-semibold">Cliente</th>
                <th className="px-5 py-3 text-left font-semibold">Producto recomendado</th>
                <th className="px-5 py-3 text-left font-semibold">Compra hace 1 año</th>
                <th className="px-5 py-3 text-right font-semibold">Monto anterior</th>
                <th className="px-5 py-3 text-center font-semibold">Días sin comprar</th>
                <th className="px-5 py-3 text-center font-semibold">Acción</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.seguimientoClientes.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-6 text-center text-xs text-fr-dark/50">
                    No hay clientes que hayan comprado en este mismo mes hace un año todavía.
                  </td>
                </tr>
              )}
              {dashboard.seguimientoClientes.map((c, i) => (
                <tr key={i} className="border-t border-fr-muted hover:bg-fr-secondary/50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-fr-primary/10 text-fr-primary text-xs font-bold flex items-center justify-center shrink-0">
                        {c.cliente.split(' ').map(p => p[0]).slice(0, 2).join('')}
                      </div>
                      <span className="font-medium text-fr-dark">{c.cliente}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-fr-dark/80">{c.producto}</td>
                  <td className="px-5 py-3 text-fr-dark/80">
                    {new Date(c.fecha_anterior).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-fr-dark">{currencyUSD(c.monto_anterior)}</td>
                  <td className="px-5 py-3 text-center">
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-fr-warning/10 text-fr-warning">
                      {c.dias_sin_comprar} días
                    </span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <button className="text-xs font-medium text-fr-primary hover:text-fr-primary-hover inline-flex items-center gap-1">
                      Contactar
                      <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Row: Clientes en riesgo + Top vendedores */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Clientes en riesgo */}
        <div className="bg-fr-light border border-fr-muted rounded-xl shadow-sm">
          <div className="px-5 py-4 border-b border-fr-muted flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-fr-danger/10 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-fr-danger text-[22px]">warning</span>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-fr-dark tracking-tight">Clientes en riesgo</h2>
                <p className="text-xs text-fr-dark/50 mt-0.5">Top clientes sin compras recientes</p>
              </div>
            </div>
          </div>
          <ul className="divide-y divide-fr-muted">
            {dashboard.clientesEnRiesgo.length === 0 && (
              <li className="px-5 py-6 text-center text-xs text-fr-dark/50">
                No hay clientes en riesgo. ¡Buena noticia!
              </li>
            )}
            {dashboard.clientesEnRiesgo.map((c, i) => (
              <li key={i} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-fr-secondary/50 transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-fr-dark truncate">{c.cliente}</p>
                  <p className="text-[11px] text-fr-dark/50">
                    Histórico: <span className="font-semibold text-fr-dark/70">{currencyUSD(c.total_historico)}</span>
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-fr-danger/10 text-fr-danger">
                    {c.dias_sin_comprar} días
                  </span>
                  <p className="text-[10px] text-fr-dark/40 mt-0.5">
                    Última: {new Date(c.ultima_compra).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Top vendedores */}
        <div className="bg-fr-light border border-fr-muted rounded-xl shadow-sm">
          <div className="px-5 py-4 border-b border-fr-muted flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-fr-success/10 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-fr-success text-[22px]">military_tech</span>
              </div>
              <div>
                <h2 className="text-sm font-semibold text-fr-dark tracking-tight">Top vendedores del mes</h2>
                <p className="text-xs text-fr-dark/50 mt-0.5">Avance vs meta individual</p>
              </div>
            </div>
          </div>
          <ul className="divide-y divide-fr-muted">
            {dashboard.topVendedores.length === 0 && (
              <li className="px-5 py-6 text-center text-xs text-fr-dark/50">
                Aún no hay ventas registradas este mes.
              </li>
            )}
            {dashboard.topVendedores.map((v, i) => {
              const progreso = v.meta > 0 ? (v.total / v.meta) * 100 : 0;
              return (
                <li key={i} className="px-5 py-3 hover:bg-fr-secondary/50 transition-colors">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-7 h-7 rounded-full bg-fr-primary text-fr-light text-xs font-bold flex items-center justify-center shrink-0">
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-fr-dark truncate">{v.vendedor}</p>
                        <p className="text-[11px] text-fr-dark/50">{v.ventas} ventas</p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-fr-dark whitespace-nowrap">{currencyUSD(v.total)}</span>
                  </div>
                  <div className="h-1.5 bg-fr-secondary rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${progreso >= 90 ? 'bg-fr-success' : progreso >= 70 ? 'bg-fr-primary' : 'bg-fr-warning'}`}
                      style={{ width: `${Math.min(progreso, 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between mt-1 text-[10px] text-fr-dark/50">
                    <span>{progreso.toFixed(0)}% de meta</span>
                    <span>Meta: {currencyUSD(v.meta)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Distribución por día de la semana */}
      <section className="bg-fr-light border border-fr-muted rounded-xl shadow-sm">
        <div className="px-5 py-4 border-b border-fr-muted">
          <h2 className="text-sm font-semibold text-fr-dark tracking-tight">Distribución por día de la semana</h2>
          <p className="text-xs text-fr-dark/50 mt-0.5">Patrón semanal de ventas (acumulado)</p>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={insights?.porDow || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="dia" tick={{ fontSize: 11, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 11, fill: '#6b7280' }}
                tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
              />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: '1px solid #d1d5db', fontSize: 12 }}
                formatter={(v) => formatCurrencyUsd(v)}
              />
              <Bar dataKey="total" fill="#078be3" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Productos sin movimiento */}
      <section className="bg-fr-light border border-fr-muted rounded-xl shadow-sm">
        <div className="px-5 py-4 border-b border-fr-muted flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-fr-warning/10 flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined text-fr-warning text-[22px]">inventory_2</span>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-fr-dark tracking-tight">Productos con baja rotación</h2>
            <p className="text-xs text-fr-dark/50 mt-0.5">Inventario que no ha tenido movimiento reciente</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-fr-secondary text-fr-dark/60 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3 text-left font-semibold">Producto</th>
                <th className="px-5 py-3 text-center font-semibold">Días sin venta</th>
                <th className="px-5 py-3 text-right font-semibold">Stock disponible</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.productosBajaRotacion.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-5 py-6 text-center text-xs text-fr-dark/50">
                    No se detectaron productos con baja rotación.
                  </td>
                </tr>
              )}
              {dashboard.productosBajaRotacion.map((p, i) => (
                <tr key={i} className="border-t border-fr-muted hover:bg-fr-secondary/50 transition-colors">
                  <td className="px-5 py-3 font-medium text-fr-dark">{p.producto}</td>
                  <td className="px-5 py-3 text-center">
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-fr-warning/10 text-fr-warning">
                      {p.dias_sin_venta !== null ? `${p.dias_sin_venta} días` : 'Sin ventas'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-fr-dark">
                    {p.stock !== null && p.stock !== undefined ? `${p.stock} pzs.` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

// ===== Componente de KPI Card =====
const KpiCard = ({ icon, label, value, variation, hint, accent = 'primary' }) => {
  const accentMap = {
    primary: 'bg-fr-primary/10 text-fr-primary',
    accent: 'bg-fr-accent/10 text-fr-accent',
    success: 'bg-fr-success/10 text-fr-success',
    warning: 'bg-fr-warning/10 text-fr-warning',
    danger: 'bg-fr-danger/10 text-fr-danger',
  };
  const positive = (variation ?? 0) >= 0;

  return (
    <div className="bg-fr-light border border-fr-muted rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold tracking-[0.15em] text-fr-dark/40 uppercase">{label}</p>
          <p className="text-2xl md:text-[28px] font-bold text-fr-dark tracking-tight mt-1.5 break-words">
            {value}
          </p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${accentMap[accent]}`}>
          <span className="material-symbols-outlined text-[22px]">{icon}</span>
        </div>
      </div>
      {variation !== undefined && variation !== null && (
        <div className="flex items-center gap-2 mt-3">
          <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded
            ${positive ? 'text-fr-success bg-fr-success/10' : 'text-fr-danger bg-fr-danger/10'}`}>
            <span className="material-symbols-outlined text-[14px]">
              {positive ? 'trending_up' : 'trending_down'}
            </span>
            {Math.abs(variation).toFixed(1)}%
          </span>
          <span className="text-[11px] text-fr-dark/50">{hint}</span>
        </div>
      )}
    </div>
  );
};

export default Home;
